/**
 * CSV Export Utility
 * Handles exporting documents to CSV format
 */
import type { CustomFieldValueDto, DocumentDetailDto, LineItemDto } from "@/types/api/documents";
import { getCustomFieldDtoDisplayValue } from "@/types/api/documents";

/**
 * Escape CSV field value
 * Wraps in quotes if contains comma, newline, or quote
 */
function escapeCsvValue(value: string | number | boolean | null | undefined): string {
    if (value === null || value === undefined) {
        return "";
    }

    const stringValue = String(value);

    // Check if value needs to be quoted (contains comma, newline, or quote)
    if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
        // Escape quotes by doubling them
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
}

/**
 * Build CSV header row from custom field definitions
 */
function buildCsvHeaders(documents: DocumentDetailDto[]): string[] {
    const fieldCodesSet = new Set<string>();
    const fieldCodeToOrder = new Map<string, number>();

    // Collect all unique exportable field codes from all documents
    documents.forEach((doc) => {
        doc.customFieldValues?.forEach((fieldValue) => {
            if (!fieldValue?.isExportable) {
                return;
            }

            const fieldCode = fieldValue.fieldCode;
            const displayOrder = fieldValue.displayOrder ?? 999;

            if (fieldCode) {
                fieldCodesSet.add(fieldCode);
                if (!fieldCodeToOrder.has(fieldCode)) {
                    fieldCodeToOrder.set(fieldCode, displayOrder);
                }
            }
        });
    });

    // Sort field codes by display order
    const sortedFieldCodes = Array.from(fieldCodesSet).sort((a, b) => {
        const orderA = fieldCodeToOrder.get(a) ?? 999;
        const orderB = fieldCodeToOrder.get(b) ?? 999;
        return orderA - orderB;
    });

    return sortedFieldCodes;
}

/**
 * Build CSV row for a document
 */
function buildCsvRow(document: DocumentDetailDto, fieldCodes: string[]): string[] {
    // Create a map of exportable field code to value for quick lookup
    const fieldValueMap = new Map<string, CustomFieldValueDto>();

    document.customFieldValues?.forEach((fieldValue) => {
        const fieldCode = fieldValue?.fieldCode;
        if (fieldCode && fieldValue.isExportable) {
            fieldValueMap.set(fieldCode, fieldValue);
        }
    });

    // Build row values in the same order as headers
    return fieldCodes.map((fieldCode) => {
        const fieldValue = fieldValueMap.get(fieldCode);
        if (fieldValue) {
            const value = getCustomFieldDtoDisplayValue(fieldValue);
            return escapeCsvValue(value);
        }
        return "";
    });
}

/**
 * Build the ordered list of line-item field codes across all documents' line items.
 * One consistent column order is used for every DDOC row in the file.
 */
function buildLineItemFieldCodes(documents: DocumentDetailDto[]): string[] {
    const fieldCodesSet = new Set<string>();
    const fieldCodeToOrder = new Map<string, number>();

    // Collect all unique exportable line-item field codes from all documents' line items
    documents.forEach((doc) => {
        doc.lineItems?.forEach((lineItem) => {
            lineItem.customFieldValues?.forEach((fieldValue) => {
                if (!fieldValue?.isExportable) {
                    return;
                }

                const fieldCode = fieldValue.fieldCode;
                const displayOrder = fieldValue.displayOrder ?? 999;

                if (fieldCode) {
                    fieldCodesSet.add(fieldCode);
                    if (!fieldCodeToOrder.has(fieldCode)) {
                        fieldCodeToOrder.set(fieldCode, displayOrder);
                    }
                }
            });
        });
    });

    // Sort field codes by display order
    return Array.from(fieldCodesSet).sort((a, b) => {
        const orderA = fieldCodeToOrder.get(a) ?? 999;
        const orderB = fieldCodeToOrder.get(b) ?? 999;
        return orderA - orderB;
    });
}

/**
 * Build CSV row for a single line item
 */
function buildLineItemRow(lineItem: LineItemDto, fieldCodes: string[]): string[] {
    // Create a map of exportable field code to value for quick lookup
    const fieldValueMap = new Map<string, CustomFieldValueDto>();

    lineItem.customFieldValues?.forEach((fieldValue) => {
        const fieldCode = fieldValue?.fieldCode;
        if (fieldCode && fieldValue.isExportable) {
            fieldValueMap.set(fieldCode, fieldValue);
        }
    });

    // Build row values in the same order as headers
    return fieldCodes.map((fieldCode) => {
        const fieldValue = fieldValueMap.get(fieldCode);
        if (fieldValue) {
            const value = getCustomFieldDtoDisplayValue(fieldValue);
            return escapeCsvValue(value);
        }
        return "";
    });
}

/**
 * Export documents to CSV and trigger download
 */
export function exportDocumentsToCsv(documents: DocumentDetailDto[], filename: string = "documents.csv"): void {
    if (documents.length === 0) {
        console.warn("No documents to export");
        return;
    }

    console.log("Exporting documents:", documents.length);
    console.log("First document:", documents[0]);
    console.log("Custom field values:", documents[0].customFieldValues);

    // Build CSV headers
    const fieldCodes = buildCsvHeaders(documents);
    console.log("Field codes:", fieldCodes);

    if (fieldCodes.length === 0) {
        console.error("No custom fields found in documents");
        alert("No custom fields found to export. Documents may not have been parsed yet.");
        return;
    }

    // Build the line-item column order, shared across every DDOC row in this file.
    const lineItemFieldCodes = buildLineItemFieldCodes(documents);

    // Build output rows (no header row). Per document: one "HDOC" row with the document
    // field values in display order, followed by one "DDOC" row per line item with the
    // line-item field values in display order. "HDOC"/"DDOC" are plain literals, so they
    // need no escaping. Documents with no line items emit only their "HDOC" row.
    const dataRows: string[] = [];
    documents.forEach((doc) => {
        const rowValues = buildCsvRow(doc, fieldCodes);
        dataRows.push(["HDOC", ...rowValues].join(","));

        doc.lineItems?.forEach((lineItem) => {
            const lineItemValues = buildLineItemRow(lineItem, lineItemFieldCodes);
            dataRows.push(["DDOC", ...lineItemValues].join(","));
        });
    });

    console.log("Data rows:", dataRows);

    // Combine HDOC/DDOC rows (no header)
    const csvContent = dataRows.join("\n");
    console.log("CSV Content length:", csvContent.length);
    console.log("CSV Content preview:", csvContent.substring(0, 200));

    // Create blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
}

/**
 * Export documents grouped by document type
 * Creates separate CSV files for each document type
 */
export function exportDocumentsByType(documents: DocumentDetailDto[], timestamp: string): void {
    if (documents.length === 0) {
        console.warn("No documents to export");
        return;
    }

    console.log("Exporting documents by type:", documents.length);

    // Group documents by document type name
    const documentsByType = new Map<string, DocumentDetailDto[]>();

    documents.forEach((doc) => {
        // Get document type name, fallback to 'unknown' if not available
        const typeName = doc.documentType?.name || doc.documentType?.code || "unknown";

        if (!documentsByType.has(typeName)) {
            documentsByType.set(typeName, []);
        }
        documentsByType.get(typeName)!.push(doc);
    });

    console.log("Grouped by types:", Array.from(documentsByType.keys()));

    // Export each group to a separate CSV file
    documentsByType.forEach((docs, typeName) => {
        console.log(`Exporting ${docs.length} documents of type: ${typeName}`);

        // Create a safe filename from the document type name
        const safeTypeName = typeName
            .toLowerCase()
            .replace(/\s+/g, "-") // Replace spaces with dashes
            .replace(/[^a-z0-9-]/g, ""); // Remove special characters

        const filename = `${safeTypeName}-export-${timestamp}.csv`;

        // Export this group of documents
        exportDocumentsToCsv(docs, filename);
    });

    console.log(`Successfully exported ${documentsByType.size} CSV file(s)`);
}
